<script setup lang="ts">import { computed as _computed } from 'vue';

import {
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui";

import { SortBy, SortOrder } from "@/models/Filter";
import { useUserSessionStore } from "@/stores/user-session-store";

const SortByLabels = {
  [SortBy.FILE_NAME]: "File Name",
  [SortBy.DATE_ISSUED]: "Date Issued",
  [SortBy.CLASSIFICATION]: "Classification",
  [SortBy.FILE_TYPE]: "File Type",
  [SortBy.PAGE_COUNT]: "Page Count",
};

const sort = _computed(() => useUserSessionStore().brandSort);

async function changeSorting(sortBy: SortBy) {
  let sortOrder = SortOrder.ASC;
  if (sort.value.sortBy === sortBy && sort.value.sortOrder === SortOrder.ASC) {
    sortOrder = SortOrder.DESC;
  }

  const { brandLayout: layout } = useUserSessionStore();

  await useUserSessionStore().saveBrandSession({ layout, sort: { sortBy, sortOrder } });
}
</script>
<template>
  <SelectRoot :model-value="sort.sortBy" @update:model-value="(value) => changeSorting(value as SortBy)">
    <SelectTrigger
      class="tw-btn-md-gray tw-flex tw-min-w-0 tw-items-center"
      :aria-label="`Sorting by ${SortByLabels[sort.sortBy]} in ${sort.sortOrder === SortOrder.DESC ? 'descending' : 'ascending'} order`"
    >
      <SelectValue class="tw-grow tw-truncate">
        Sort by {{ SortByLabels[sort.sortBy] }}
        <i
          :class="[
            { 'fa-sort-alpha-up': sort.sortOrder === SortOrder.DESC },
            { 'fa-sort-alpha-down': sort.sortOrder === SortOrder.ASC },
            'fas fa-fw',
          ]"
        />
      </SelectValue>
      <SelectIcon>
        <i aria-hidden="true" class="fas fa-fw fa-chevron-down" />
      </SelectIcon>
    </SelectTrigger>
    <SelectPortal>
      <SelectContent
        :side-offset="4"
        position="popper"
        :avoid-collisions="true"
        class="tw-z-[100000] tw-overflow-hidden tw-rounded tw-border tw-border-gray-500 tw-bg-white tw-shadow-md"
      >
        <SelectScrollUpButton />
        <SelectViewport>
          <SelectItem
            v-for="(label, key) in SortByLabels"
            :key="key"
            class="tw-block tw-cursor-pointer tw-px-4 tw-py-1 tw-text-gray-800 hover:tw-bg-purple-100 hover:tw-text-purple-900 focus:tw-bg-gray-100 focus:tw-text-gray-800 focus:tw-outline-none"
            :value="key"
          >
            <SelectItemIndicator class="w-[25px] grid tw-absolute tw-left-1 tw-place-items-center">
              <i :aria-hidden="true" class="fas fa-check fa-xs"></i>
            </SelectItemIndicator>
            <SelectItemText class="tw-text-sm">
              {{ label }}
              <i
                v-if="sort.sortBy === key"
                :class="[
                  { 'fa-sort-alpha-up': sort.sortOrder === SortOrder.DESC },
                  { 'fa-sort-alpha-down': sort.sortOrder === SortOrder.ASC },
                  'fas fa-fw',
                ]"
              ></i>
            </SelectItemText>
          </SelectItem>
        </SelectViewport>
        <SelectScrollDownButton />
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
